import { Project, api } from '@common';
import { useAuth } from '@hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

// Interface for creating a new project
type NewProjectInput = {
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  budget?: string;
  siteArea?: string;
  earthMoved?: string;
  duration?: string;
  completionYear?: number;
  isHighlight?: boolean;
  image?: File;
  // Big numbers display configuration
  showDuration?: boolean;
  showYear?: boolean;
  showBudget?: boolean;
  showEarthMoved?: boolean;
};

// Interface for updating a project
type UpdateProjectInput = Partial<NewProjectInput>;

// Interface for project query parameters
interface ProjectsParams {
  page?: number;
  limit?: number;
  search?: string;
  isHighlight?: boolean;
  orderBy?: string;
  orderType?: 'asc' | 'desc';
}

// Interface for project statistics
interface ProjectStats {
  total: number;
  archived: number;
  highlighted: number;
  recentProjects: number;
}

// Interface for paginated response
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

export const useProjects = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  // Fetch all projects with optional parameters
  const projects = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: async () => {
      try {
        const response = await api.get('/projects');
        // Handle both array response and paginated response
        if (response.data && typeof response.data === 'object' && 'data' in response.data) {
          return response.data.data;
        }
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        message.error('Error retrieving projects');
        throw error;
      }
    }
  });

  // Fetch projects with filters and pagination
  const getProjectsWithFilters = (params: ProjectsParams) => {
    return useQuery<PaginatedResponse<Project>>({
      queryKey: ['projects', params],
      queryFn: async () => {
        try {
          const response = await api.get('/projects', { params });
          return response.data;
        } catch (error) {
          message.error('Error retrieving projects');
          throw error;
        }
      }
    });
  };

  // Fetch highlighted projects
  const getHighlightedProjects = (params?: Omit<ProjectsParams, 'isHighlight'>) => {
    return useQuery<PaginatedResponse<Project>>({
      queryKey: ['projects', 'highlighted', params],
      queryFn: async () => {
        try {
          const response = await api.get('/projects/highlighted', { params });
          return response.data;
        } catch (error) {
          message.error('Error retrieving highlighted projects');
          throw error;
        }
      }
    });
  };

  // Fetch a specific project by ID
  const getProject = (id: string) => {
    return useQuery<Project>({
      queryKey: ['project', id],
      queryFn: async () => {
        try {
          const response = await api.get(`/projects/${id}`);
          return response.data;
        } catch (error) {
          message.error('Error retrieving project details');
          throw error;
        }
      },
      enabled: !!id
    });
  };

  // Fetch project statistics (admin only)
  const getProjectStats = () => {
    return useQuery<ProjectStats>({
      queryKey: ['projects', 'stats'],
      queryFn: async () => {
        try {
          const response = await api.get('/projects/stats');
          return response.data;
        } catch (error) {
          message.error('Error retrieving project statistics');
          throw error;
        }
      },
      enabled: isAuthenticated
    });
  };

  // Fetch archived projects (admin only)
  const getArchivedProjects = (params?: ProjectsParams) => {
    return useQuery<PaginatedResponse<Project>>({
      queryKey: ['projects', 'archived', params],
      queryFn: async () => {
        try {
          const response = await api.get('/projects/archived', { params });
          return response.data;
        } catch (error) {
          message.error('Error retrieving archived projects');
          throw error;
        }
      },
      enabled: isAuthenticated
    });
  };

  // Add a new project (admin only)
  const addProject = useMutation({
    mutationFn: async (project: NewProjectInput) => {
      try {
        // Check if there's an image file
        const hasImageFile = project.image instanceof File;
        
        if (hasImageFile) {
          // Use FormData when there's an image
          const formData = new FormData();
          
          Object.entries(project).forEach(([key, value]) => {
            // Skip undefined and null, but allow false and other falsy values
            if (value === undefined || value === null) {
              return;
            }
            
            if (key === 'image' && value instanceof File) {
              formData.append('image', value);
            } else {
              formData.append(key, value.toString());
            }
          });

          const response = await api.post('/projects', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          return response.data;
        } else {
          // Use JSON when there's no image - this preserves boolean types
          const { image, ...projectWithoutImage } = project;
          
          const response = await api.post('/projects', projectWithoutImage, {
            headers: {
              'Content-Type': 'application/json',
            },
          });
          return response.data;
        }
      } catch (error) {
        console.error('API error adding project:', error);
        message.error('Error adding project');
        throw error;
      }
    },
    onSuccess: () => {
      message.success('Project added successfully!');
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    }
  });

  // Update a project (admin only)
  const updateProject = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProjectInput }) => {
      try {
        // Check if there's an image file
        const hasImageFile = data.image instanceof File;
        
        if (hasImageFile) {
          // Use FormData when there's an image
          const formData = new FormData();
          
          Object.entries(data).forEach(([key, value]) => {
            // Skip undefined and null, but allow false and other falsy values
            if (value === undefined || value === null) {
              return;
            }
            
            if (key === 'image' && value instanceof File) {
              formData.append('image', value);
            } else {
              formData.append(key, value.toString());
            }
          });

          const response = await api.patch(`/projects/${id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          return response.data;
        } else {
          // Use JSON when there's no image - this preserves boolean types
          const { image, ...dataWithoutImage } = data;
          
          const response = await api.patch(`/projects/${id}`, dataWithoutImage, {
            headers: {
              'Content-Type': 'application/json',
            },
          });
          return response.data;
        }
      } catch (error) {
        console.error('API error updating project:', error);
        message.error('Error updating project');
        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      message.success('Project updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.id] });
    }
  });

  // Archive a project (admin only)
  const archiveProject = useMutation({
    mutationFn: async (projectId: string) => {
      try {
        const response = await api.patch(`/projects/${projectId}/archive`);
        return response.data;
      } catch (error) {
        message.error('Error archiving project');
        throw error;
      }
    },
    onSuccess: (_, projectId) => {
      message.success('Project archived successfully!');
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    }
  });

  // Restore an archived project (admin only)
  const restoreProject = useMutation({
    mutationFn: async (projectId: string) => {
      try {
        const response = await api.patch(`/projects/${projectId}/restore`);
        return response.data;
      } catch (error) {
        message.error('Error restoring project');
        throw error;
      }
    },
    onSuccess: (_, projectId) => {
      message.success('Project restored successfully!');
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'archived'] });
    }
  });

  // Delete a project permanently (admin only)
  const deleteProject = useMutation({
    mutationFn: async (projectId: string) => {
      try {
        const response = await api.delete(`/projects/${projectId}`);
        return response.data;
      } catch (error) {
        message.error('Error deleting project');
        throw error;
      }
    },
    onSuccess: (_, projectId) => {
      message.success('Project deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    }
  });

  // Toggle project highlight status (admin only)
  const toggleProjectHighlight = useMutation({
    mutationFn: async (projectId: string) => {
      try {
        const response = await api.patch(`/projects/${projectId}/toggle-highlight`);
        return response.data;
      } catch (error) {
        message.error('Error toggling project highlight status');
        throw error;
      }
    },
    onSuccess: (data, projectId) => {
      message.success(data.message || 'Project highlight status updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'highlighted'] });
    }
  });

  return {
    projects,
    getProject,
    getProjectsWithFilters,
    getHighlightedProjects,
    getProjectStats,
    getArchivedProjects,
    addProject,
    updateProject,
    archiveProject,
    restoreProject,
    deleteProject,
    toggleProjectHighlight
  };
}; 